// Ephemeral "who is typing" per thread. Fed by chat-realtime-service from the backend's
// typing relay ({action:"typing", userId, threadId} pushed to the other thread members).
// There is no "stopped typing" event — entries expire locally after TYPING_TTL_MS, which
// pairs with the sender's ~3s re-send throttle while they keep typing.

const TYPING_TTL_MS = 6000;
const SWEEP_INTERVAL_MS = 1000;

/** @type {Map<string, Map<string, number>>} threadId -> (userId -> expiresAt epoch ms) */
let typingByThread = new Map();
const listeners = new Set();
let sweepTimer = null;

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // ignore listener faults
    }
  }
}

function sweep() {
  sweepTimer = null;
  const now = Date.now();
  let changed = false;
  for (const [threadId, users] of typingByThread) {
    for (const [userId, expiresAt] of users) {
      if (expiresAt <= now) {
        users.delete(userId);
        changed = true;
      }
    }
    if (users.size === 0) {
      typingByThread.delete(threadId);
    }
  }
  if (typingByThread.size > 0) {
    sweepTimer = setTimeout(sweep, SWEEP_INTERVAL_MS);
  }
  if (changed) {
    notify();
  }
}

/** Applies a typing event forwarded by chat-realtime-service. */
export function applyTypingRealtime({ threadId, userId } = {}) {
  const tid = String(threadId || "").trim();
  const uid = String(userId || "").trim();
  if (!tid || !uid) return;
  let users = typingByThread.get(tid);
  if (!users) {
    users = new Map();
    typingByThread.set(tid, users);
  }
  users.set(uid, Date.now() + TYPING_TTL_MS);
  if (!sweepTimer) {
    sweepTimer = setTimeout(sweep, SWEEP_INTERVAL_MS);
  }
  notify();
}

/** Unexpired typing user ids for a thread (excluding no one — callers filter self). */
export function getTypingUserIds(threadId) {
  const users = typingByThread.get(String(threadId || "").trim());
  if (!users || users.size === 0) return [];
  const now = Date.now();
  const out = [];
  for (const [userId, expiresAt] of users) {
    if (expiresAt > now) out.push(userId);
  }
  return out;
}

/** Subscribe to typing changes (coarse — no payload; read via getTypingUserIds). */
export function subscribeTyping(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}
